import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    RestaurantMenu as RestaurantMenuIcon,
    AttachMoney as AttachMoneyIcon,
    PeopleAlt as PeopleAltIcon,
    Kitchen as KitchenIcon,
    AccessTime as AccessTimeIcon,
    Star as StarIcon,
    CheckCircle as CheckCircleIcon,
    LocalDining as LocalDiningIcon,
    FavoriteBorder as FavoriteBorderIcon,
    ArrowForwardIos as ArrowForwardIosIcon
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'ルームシェアの食事問題——なぜ悩みやすいのか' },
    { id: 'shared-ingredients', label: '食材の共有・分担をどう決める？実践ポイント' },
    { id: 'cooking-rotation', label: '自炊ローテーションの作り方とメリット' },
    { id: 'expense-management', label: 'スマートな食費管理テクニック' },
    { id: 'taste-differences', label: '好みの違い・アレルギー問題のスマートな対策' },
    { id: 'quick-recipes', label: '忙しい人向け！時短レシピのすすめ' },
    { id: 'expert-tips', label: 'ルームシェア自炊生活を楽しむためのプロのアドバイス' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {/* 目次 */}
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center', minHeight: 36 }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#888',
                                        fontWeight: 'bold',
                                        fontSize: '1.05rem',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.97rem', textAlign: 'left', p: 0, m: 0 }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}
            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアの食事問題——なぜ悩みやすいのか
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア生活で「食事」は思いのほか大きなテーマです。「毎日外食も出費がかさむ」「自炊したいけど、誰が作る？」「食材をシェアするとお金の管理が難しい」——そんな悩み、ありませんか？<br />
                    本記事では、食材の分担や自炊のローテーション、食費管理のテクニック、味の好みの違いへの対策、さらに忙しい人向けの時短レシピまで、実践的で役立つノウハウをまとめてご紹介します。<br />
                    ルームシェアの食事問題をスマートに乗り越え、心地よい共同生活を実現しましょう！
                </Typography>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/sharemeal-scene1_roomshare-meal-tips.jpg"
                        alt="明るい共有リビングでルームメイト同士が食卓を囲み、色とりどりの料理を分け合いながら会話を楽しむ様子。温かい雰囲気で協力的な食事シーンが伝わる、木製テーブルと自然光が印象的"
                        style={{ width: '100%', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}
                    />
                </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            {/* 食材の共有・分担 */}
            <Box id="shared-ingredients" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <KitchenIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    食材の共有・分担をどう決める？実践ポイント
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    食材を無駄なく分担し、ストレスの少ないシェアを実現するにはコツがあります。ここでは、食材の共有方法・分担ルールの決め方を解説します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                        <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                食材シェアの基本ルール
                            </Typography>
                            <List>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="調味料や米、油など『共用』が合理的なものは全員で購入"
                                        secondary="消耗品の割り勘で無駄な重複や出費をカット"
                                    />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="メイン食材（肉・魚・野菜など）は献立や希望によって分担"
                                        secondary="『今週はAさんが野菜、Bさんが肉』などローテ制もおすすめ"
                                    />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="私物食材は名前ラベルで明確に"
                                        secondary="おやつ・特別な調味料などは各自保管、誤食防止にも"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#fff' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold' }}>
                                【シェア食材の例】
                            </Typography>
                            <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                <Chip label="米" variant="outlined" color="primary" sx={{ mr: 0.5 }} />
                                <Chip label="調味料" variant="outlined" color="primary" sx={{ mr: 0.5 }} />
                                <Chip label="パスタ" variant="outlined" color="primary" sx={{ mr: 0.5 }} />
                                <Chip label="冷凍食品" variant="outlined" color="primary" sx={{ mr: 0.5 }} />
                                <Chip label="共用野菜" variant="outlined" color="primary" sx={{ mr: 0.5 }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                ※お菓子や高価な食材、飲み物は「各自」管理が安心です。
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 3, mb: 2 }}>

                </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            {/* 自炊ローテーション */}
            <Box id="cooking-rotation" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <PeopleAltIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    自炊ローテーションの作り方とメリット
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    みんなで自炊をする場合、「誰がいつ料理するか？」はトラブルの火種にもなりがち。そこで活躍するのが「自炊ローテーション」です。
                </Typography>
                <Paper sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: '#f1f8e9' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        自炊ローテーションのパターン例
                    </Typography>
                    <List>
                        <ListItem sx={{ pl: 0 }}>
                            <ListItemIcon>
                                <StarIcon color="secondary" />
                            </ListItemIcon>
                            <ListItemText primary="曜日ごと交代制" secondary="例：月・水はAさん、火・木はBさん、金曜は2人でなど" />
                        </ListItem>
                        <ListItem sx={{ pl: 0 }}>
                            <ListItemIcon>
                                <StarIcon color="secondary" />
                            </ListItemIcon>
                            <ListItemText primary="週替わり担当制" secondary="1週間ごとに担当をローテーション" />
                        </ListItem>
                        <ListItem sx={{ pl: 0 }}>
                            <ListItemIcon>
                                <StarIcon color="secondary" />
                            </ListItemIcon>
                            <ListItemText primary="作り置き担当制" secondary="休日に1人がまとめて作り置き、他の人が消費" />
                        </ListItem>
                        <ListItem sx={{ pl: 0 }}>
                            <ListItemIcon>
                                <StarIcon color="secondary" />
                            </ListItemIcon>
                            <ListItemText primary="自由参加制" secondary="作りたい人が当日グループチャットで声掛け" />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    自炊ローテーションを決めると、負担や不公平感がぐっと減り、「誰かに料理を押し付けてしまった…」という罪悪感も少なくなります。<br />
                    また、料理の得意・不得意もカバーしやすくなり、お互いに新たなレシピを学ぶ機会にも！
                </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            {/* 食費管理テクニック */}
            <Box id="expense-management" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <AttachMoneyIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    スマートな食費管理テクニック
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    食事をシェアするなら、食費の分担・精算方法もクリアにしておきたいところ。おすすめのテクニックをご紹介します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                        <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#fffde7' }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                みんなが納得！食費管理のアイデア
                            </Typography>
                            <List>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="共用財布 or 共同口座を用意" secondary="現金なら共用財布、キャッシュレス派はアプリで管理" />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="家計簿アプリで記録" secondary="OshidOriなどが便利" />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="毎月定額で徴収し、余った分は翌月繰越" secondary="余剰金トラブル防止＆管理がシンプル" />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="レシート写真共有" secondary="グループチャットでレシートを撮って共有、透明性UP" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#fff' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                【おすすめアプリ】
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Chip label="LINE家計簿" color="success" variant="outlined" />
                                <Chip label="MoneyForward" color="success" variant="outlined" />
                                <Chip label="Splitwise" color="success" variant="outlined" />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    ポイントは「曖昧にしないこと」。記録と精算を仕組み化すれば、信頼感もアップします。
                </Typography>
                <Box sx={{ mt: 3, mb: 2 }}>

                </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            {/* 好みの違い・アレルギー問題 */}
            <Box id="taste-differences" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <FavoriteBorderIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    好みの違い・アレルギー問題のスマートな対策
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    食の好みやアレルギー、ベジタリアン・宗教的な制限など、ルームシェアならではの課題も。ストレスを減らすには「前提の共有」と「工夫」が大切です。
                </Typography>
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#f3e5f5', mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        好み・制限のミニヒアリングをしよう
                    </Typography>
                    <List>
                        <ListItem sx={{ pl: 0 }}>
                            <ListItemIcon>
                                <ArrowForwardIosIcon fontSize="small" color="disabled" />
                            </ListItemIcon>
                            <ListItemText primary="嫌いな食材・食べられないものを書き出しておく" />
                        </ListItem>
                        <ListItem sx={{ pl: 0 }}>
                            <ListItemIcon>
                                <ArrowForwardIosIcon fontSize="small" color="disabled" />
                            </ListItemIcon>
                            <ListItemText primary="「絶対NG」「できれば避けたい」を区別" />
                        </ListItem>
                        <ListItem sx={{ pl: 0 }}>
                            <ListItemIcon>
                                <ArrowForwardIosIcon fontSize="small" color="disabled" />
                            </ListItemIcon>
                            <ListItemText primary="調理担当の人が事前に確認しやすいよう、キッチンやグループチャットに貼り出す" />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" color="text.secondary">
                    みんなが納得するポイントは「無理して合わせない」「情報をオープンに」。丼ものや鍋料理なら、個別盛りや後乗せスタイルで好み調整も簡単です。
                </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            {/* 時短レシピ */}
            <Box id="quick-recipes" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <AccessTimeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    忙しい人向け！時短レシピのすすめ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    「帰宅がバラバラ」「平日は忙しい」——そんなルームシェア生活でも、手軽で美味しい自炊は可能です。ここではみんなで使える時短レシピとコツをご紹介します。
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#e3f2fd' }}>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    みんなで作れる！時短レシピ例
                                </Typography>
                                <List>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemIcon>
                                            <LocalDiningIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="ワンパンパスタ" secondary="フライパンひとつでOK、洗い物も最小限" />
                                    </ListItem>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemIcon>
                                            <LocalDiningIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="具だくさん豚汁" secondary="野菜もお肉も一緒に煮込むだけ" />
                                    </ListItem>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemIcon>
                                            <LocalDiningIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="冷凍うどんアレンジ" secondary="冷蔵庫の余り野菜と一緒に時短調理" />
                                    </ListItem>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemIcon>
                                            <LocalDiningIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="炊飯器カレー" secondary="食材とルーを炊飯器に入れるだけ" />
                                    </ListItem>
                                </List>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#fff' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    【時短調理のコツ】
                                </Typography>
                                <List>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemIcon>
                                            <AccessTimeIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="野菜はまとめてカット、冷凍保存" />
                                    </ListItem>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemIcon>
                                            <AccessTimeIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="下ごしらえ済みの冷凍食品を活用" />
                                    </ListItem>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemIcon>
                                            <AccessTimeIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="電子レンジ調理を取り入れる" />
                                    </ListItem>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemIcon>
                                            <AccessTimeIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="みんなで分担すればさらに時短" />
                                    </ListItem>
                                </List>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
                <Box sx={{ mt: 2, mb: 2 }}>
                    <img
                        src="/images/blog/quick-recipe_roomshare-meal-tips.jpg"
                        alt="カラフルな野菜とパスタを使ったワンパン料理を、ルームメイトが協力して短時間で調理している様子。モダンなキッチン、明るい雰囲気が印象的"
                        style={{ width: '100%', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                    />
                </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            {/* アドバイス */}
            <Box id="expert-tips" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <RestaurantMenuIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    ルームシェア自炊生活を楽しむためのプロのアドバイス
                </Typography>
                <Paper sx={{ p: 3, borderRadius: 2, bgcolor: '#f5f5f5' }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        ルームシェアにおける食事の工夫は、単なる「節約」や「効率化」だけでなく、生活の質や人間関係にもダイレクトに影響します。<br />
                        どんなルールも「みんなで話して決める」ことが大切。食材分担やローテーションがうまく機能しないときも、「どうしたらもっと楽になる？」と定期的に話し合いましょう。
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <List>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="完璧を目指さず、ゆるく柔軟に！"
                                        secondary="お互いの負担感をみながら調整しよう"
                                    />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="「ありがとう」「ごちそうさま」を言葉で伝える"
                                        secondary="小さな感謝の積み重ねが関係性を良くします"
                                    />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="新しいレシピやイベントを提案してみる"
                                        secondary="一緒に作る・食べる楽しさが増します"
                                    />
                                </ListItem>
                            </List>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <List>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="「今日は自分の分だけ」でOKの日も作る"
                                        secondary="無理せず、お互いのペースを尊重"
                                    />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="困ったら第三者の意見を参考に"
                                        secondary="SNSやブログで同じ悩みを検索しヒントにしよう"
                                    />
                                </ListItem>
                            </List>
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                        食事は毎日のことだからこそ、ちょっとした工夫で生活も人間関係もぐっと快適になります。<br />
                        ぜひ今回のノウハウを実践し、みなさんのルームシェア生活がより楽しく充実した時間になることを願っています！
                    </Typography>
                </Paper>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-meal-tips',
    slug: 'roomshare-meal-tips',
    title: 'ルームシェアの食事問題を解決！共同自炊のコツと食費節約術',
    excerpt: 'ルームシェアでの食材の共有や分担、自炊ローテーション、食費管理テクニック、好みの違い対策、時短レシピまで、実践的なノウハウと節約術をまとめて解説します。',
    coverImage: '/images/blog/cover-roomshare-meal_roomshare-meal-tips.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。広いテーブルの上には色鮮やかな料理が並び、協力的な共同生活の温かさが伝わる。',
    publishedAt: '2024-06-15T09:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '自炊・家事', '節約術'],
    tags: ['ルームシェア', '食費節約', '自炊', '共同生活', '家計管理'],
    author: {
        name: 'ルームシェア暮らし編集部',
        avatar: '/images/avatars/roomshare-expert.jpg',
        bio: 'ルームシェア・共同生活の課題解決を専門に、数多くの現場と声を取材。暮らしを豊かにするノウハウを発信中。',
        profilePicture: '/images/avatars/roomshare-expert.jpg'
    },
    viewCount: 2341,
    contentComponent: PostContent
};

export default post;